import styled from "styled-components";
import { TablePageContainer } from "components/TablePageLayout";
import { pxToRem, withAlpha } from "../../styles/utils";

export const SupportResponsePageContainer = styled(TablePageContainer)``;

export const ContentWrapper = styled.div`
  min-width: 0;
  flex: 1;
  box-sizing: border-box;
  overflow: auto;
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)} ${({ theme }) => pxToRem(theme.spacing.xxl)} ${pxToRem(48)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.md}, ${({ theme }) => theme.shadows.ring};

  @media (max-width: 720px) {
    padding: ${({ theme }) => pxToRem(theme.spacing.lg)};
  }
`;

export const ResponseBody = styled.main`
  width: 100%;
  max-width: ${pxToRem(950)};
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.inkSoft};
  font: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 400;
  line-height: 1.35;
  cursor: pointer;
  transition: color 0.15s ease;

  svg {
    width: ${pxToRem(19)};
    height: ${pxToRem(19)};
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  margin: ${({ theme }) => pxToRem(theme.spacing.lg)} 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

export const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xxl)};
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const StatusBadge = styled.span<{ $answered: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: ${pxToRem(31)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ $answered, theme }) =>
    $answered ? withAlpha(theme.colors.success, 0.14) : withAlpha(theme.colors.warning, 0.14)};
  color: ${({ $answered, theme }) => ($answered ? theme.colors.success : theme.colors.warningText)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;

export const Meta = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xxl)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.4;
`;

export const Label = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 400;
  line-height: 1.35;
`;

export const ResponseText = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 400;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const QuestionBlock = styled.section`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xl)};
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)} ${({ theme }) => pxToRem(theme.spacing.xl)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const AttachmentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  max-width: 100%;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xs)} ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.35;
  text-decoration: none;
  overflow-wrap: anywhere;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;

  svg {
    flex: 0 0 auto;
    width: ${pxToRem(15)};
    height: ${pxToRem(20)};
    color: ${({ theme }) => theme.colors.brand};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brandDark};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const AnswerBlock = styled.section`
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)} ${({ theme }) => pxToRem(theme.spacing.xl)};
  border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.brand, 0.35)};
  border-left-width: ${pxToRem(4)};
  border-radius: ${({ theme }) =>
    `${pxToRem(theme.radii.sm)} ${pxToRem(theme.radii.lg)} ${pxToRem(theme.radii.lg)} ${pxToRem(theme.radii.sm)}`};
  background: ${({ theme }) => theme.colors.brandSoft};
`;

export const AnswerDate = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.35;
`;

export const WaitingBlock = styled.section`
  padding: ${({ theme }) => pxToRem(theme.spacing.xl)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  line-height: 1.45;
`;

export const PageState = styled.div`
  display: flex;
  min-height: ${pxToRem(180)};
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
`;
